import React, { useEffect, useState } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { getAPIBaseURL } from "../../services/helpers"
import { getData, updateData, deleteData } from "../../services/request"
import {
    BuildingOfficeIcon,
    EnvelopeIcon,
    PhoneIcon,
    MapPinIcon,
    ArrowLeftIcon,
    TrashIcon,
    PencilIcon
} from "@heroicons/react/24/outline"

const AdminCenterDetails = () => {
    const { userId } = useParams()
    const navigate = useNavigate()
    const [center, setCenter] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")
    const [success, setSuccess] = useState("")

    // Edit State
    const [editing, setEditing] = useState(false)
    const [formData, setFormData] = useState({
        organization: "",
        email: "",
        phoneNumber: "",
        state: "",
        localGovernment: "",
        address: ""
    })
    const [submitting, setSubmitting] = useState(false)

    // Lists
    const [states, setStates] = useState([])
    const [localGovernments, setLocalGovernments] = useState([])

    const fetchCenter = async () => {
        setLoading(true)
        setError("")
        const url = `${getAPIBaseURL()}/v1/users`
        const params = { userId }
        try {
            const res = await getData(url, {}, params)
            if (res.result.status === 200) {
                const data = Array.isArray(res.result.data?.data) ? res.result.data.data[0] : res.result.data?.data
                setCenter(data)
                if (data) {
                    setFormData({
                        organization: data.organization || "",
                        email: data.email || "",
                        phoneNumber: data.phoneNumber || "",
                        state: data.state || "",
                        localGovernment: data.localGovernment || "",
                        address: data.address || ""
                    })
                }
            } else {
                setError("Center not found")
            }
        } catch (err) {
            setError("Failed to load center details")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchCenter()
        fetchStates()
    }, [userId])

    useEffect(() => {
        if (formData.state) fetchLocalGovernments(formData.state)
    }, [formData.state])

    const fetchStates = async () => {
        const url = `${getAPIBaseURL()}/v1/states`
        const res = await getData(url, {}, {})
        if (res.result?.status === 200) setStates(res.result.data?.data || [])
    }

    const fetchLocalGovernments = async (stateCode) => {
        const url = `${getAPIBaseURL()}/v1/localgovernments`
        const res = await getData(url, {}, { stateCode })
        if (res.result?.status === 200) setLocalGovernments(res.result.data?.data || [])
    }

    const handleUpdate = async (e) => {
        e.preventDefault()
        setSubmitting(true)
        setError("")
        setSuccess("")

        const url = `${getAPIBaseURL()}/v1/users/update-account`
        const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
        const params = new URLSearchParams()
        params.append('userId', userId)
        Object.keys(formData).forEach(key => params.append(key, formData[key]))

        try {
            const res = await updateData(url, headers, params.toString())
            if (res.result.status === 200) {
                setSuccess("Center updated successfully")
                setEditing(false)
                fetchCenter()
            } else {
                setError(res.result.data?.message || "Update failed")
            }
        } catch (err) {
            setError("Update failed")
        } finally {
            setSubmitting(false)
        }
    }

    const handleToggleStatus = async () => {
        if (!window.confirm(`Are you sure you want to ${center.status === 1 ? 'deactivate' : 'activate'} this center?`)) return

        setSubmitting(true)
        const url = `${getAPIBaseURL()}/v1/users/status`
        const headers = { 'Content-Type': 'application/x-www-form-urlencoded' }
        const params = new URLSearchParams()
        params.append('userId', userId)
        params.append('status', center.status === 1 ? '0' : '1')

        try {
            const res = await updateData(url, headers, params.toString())
            if (res.result.status === 200) {
                setSuccess(`Center ${center.status === 1 ? 'deactivated' : 'activated'} successfully`)
                fetchCenter()
            } else {
                setError("Status update failed")
            }
        } catch (err) {
            setError("Status update failed")
        } finally {
            setSubmitting(false)
        }
    }

    const handleDelete = async () => {
        if (!window.confirm("Are you sure? This cannot be undone.")) return

        setSubmitting(true)
        const url = `${getAPIBaseURL()}/v1/users`
        try {
            const res = await deleteData(url, {}, { userId })
            if (res.result.status === 200) {
                navigate('/admin/centers')
            } else {
                setError("Delete failed")
            }
        } catch (err) {
            setError("Delete failed")
        } finally {
            setSubmitting(false)
        }
    }

    if (loading) return <div className="text-center py-20">Loading...</div>
    if (!center) return <div className="text-center py-20">Center not found</div>

    return (
        <div className="max-w-4xl mx-auto">
            <Link to="/admin/centers" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 mb-6">
                <ArrowLeftIcon className="w-4 h-4" /> Back to Centers
            </Link>

            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                {/* Header */}
                <div className="bg-slate-50 px-8 py-6 border-b border-slate-200 flex justify-between items-start">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900">{center.organization || center.name}</h1>
                        <p className="text-slate-500 text-sm mt-1">ID: {center._id}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${center.status === 1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                        {center.status === 1 ? 'Active' : 'Inactive'}
                    </span>
                </div>

                <div className="p-8">
                    {error && <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-xl">{error}</div>}
                    {success && <div className="mb-6 p-4 bg-green-50 text-green-600 rounded-xl">{success}</div>}

                    {editing ? (
                        <form onSubmit={handleUpdate} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Organization Name</label>
                                    <input
                                        value={formData.organization}
                                        onChange={e => setFormData({ ...formData, organization: e.target.value })}
                                        className="w-full rounded-lg border-slate-300 focus:ring-cyan-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                                    <input
                                        value={formData.email}
                                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                                        className="w-full rounded-lg border-slate-300 focus:ring-cyan-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                                    <input
                                        value={formData.phoneNumber}
                                        onChange={e => setFormData({ ...formData, phoneNumber: e.target.value })}
                                        className="w-full rounded-lg border-slate-300 focus:ring-cyan-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                                    <input
                                        value={formData.address}
                                        onChange={e => setFormData({ ...formData, address: e.target.value })}
                                        className="w-full rounded-lg border-slate-300 focus:ring-cyan-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">State</label>
                                    <select
                                        value={formData.state}
                                        onChange={e => setFormData({ ...formData, state: e.target.value })}
                                        className="w-full rounded-lg border-slate-300 focus:ring-cyan-500"
                                    >
                                        <option value="">Select State</option>
                                        {states.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">LGA</label>
                                    <select
                                        value={formData.localGovernment}
                                        onChange={e => setFormData({ ...formData, localGovernment: e.target.value })}
                                        className="w-full rounded-lg border-slate-300 focus:ring-cyan-500"
                                    >
                                        <option value="">Select LGA</option>
                                        {localGovernments.map(lg => <option key={lg.code} value={lg.code}>{lg.name}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="flex gap-4 pt-4">
                                <button disabled={submitting} className="px-6 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800">Save Changes</button>
                                <button type="button" onClick={() => setEditing(false)} className="px-6 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50">Cancel</button>
                            </div>
                        </form>
                    ) : (
                        <div className="space-y-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-4">
                                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Contact Info</h3>
                                    <div className="flex items-start gap-3">
                                        <EnvelopeIcon className="w-5 h-5 text-slate-400 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-medium text-slate-900">Email Address</p>
                                            <p className="text-slate-600">{center.email}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <PhoneIcon className="w-5 h-5 text-slate-400 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-medium text-slate-900">Phone Number</p>
                                            <p className="text-slate-600">{center.phoneNumber || 'N/A'}</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-4">
                                    <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Location</h3>
                                    <div className="flex items-start gap-3">
                                        <MapPinIcon className="w-5 h-5 text-slate-400 mt-0.5" />
                                        <div>
                                            <p className="text-sm font-medium text-slate-900">Address</p>
                                            <p className="text-slate-600">{center.address || 'N/A'}</p>
                                            <p className="text-slate-600 mt-1">{center.localGovernment ? `${center.localGovernment}, ` : ''}{center.state} State</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-8 border-t border-slate-100 flex flex-wrap gap-4">
                                <button onClick={() => setEditing(true)} className="flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-lg text-slate-700 font-medium hover:bg-slate-50">
                                    <PencilIcon className="w-4 h-4" /> Edit Details
                                </button>
                                <button onClick={handleToggleStatus} className={`flex items-center gap-2 px-4 py-2 border rounded-lg font-medium ${center.status === 1
                                        ? 'border-yellow-200 text-yellow-700 hover:bg-yellow-50'
                                        : 'border-green-200 text-green-700 hover:bg-green-50'
                                    }`}>
                                    {center.status === 1 ? 'Deactivate Center' : 'Activate Center'}
                                </button>
                                <button onClick={handleDelete} className="ml-auto flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 rounded-lg font-medium hover:bg-red-50">
                                    <TrashIcon className="w-4 h-4" /> Delete Center
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default AdminCenterDetails
